// Die Sitzung aus Sicht des Schuelers: beitreten, Aufgaben festhalten, Stand melden.
// Ein reaktives Objekt fuer die ganze App — die Kopfzeile zeigt daraus den Namen,
// die Ansichten den Stand.
//
// daten = {
//   code, token, name, konfiguration,
//   aufgaben: [...],   // einmal gezogen, je nach Thema Funkaufgaben-Labels oder Uebungen
//   stand: [...]       // ein Eintrag pro Aufgabe, so wie der Server ihn prueft
// }
// Alles liegt in localStorage: ein Neuladen gibt denselben Schueler zurueck.

import { reactive } from 'vue'
import { beitreten, standSenden, ichPruefen } from './sitzungApi'

const SPEICHER = 'funktrainer.schueler'
const WIEDERHOLEN_MS = 5000
const NACHFRAGEN_MS = 20000

function lesen() {
    try {
        return JSON.parse(localStorage.getItem(SPEICHER))
    } catch {
        return null
    }
}

function schreiben(wert) {
    try {
        if (wert) localStorage.setItem(SPEICHER, JSON.stringify(wert))
        else localStorage.removeItem(SPEICHER)
    } catch {
        // Kein Speicher: die Sitzung laeuft, nur ein Neuladen verliert sie.
    }
}

export const schuelerSitzung = reactive({
    daten: lesen(),
    // Der Server kennt Code oder Token nicht mehr: Sitzung beendet oder abgelaufen.
    beendet: false
})

// Liefert { ok: true } oder { fehler: 'Text fuer den Schueler' }.
export async function mitCodeBeitreten(eingabe) {
    const code = eingabe.trim().toUpperCase()
    const { daten, status } = await beitreten(code)
    if (!daten) {
        return {
            fehler: status === 404 ? 'Diesen Code gibt es nicht. Schau nochmal an die Wandtafel.'
                : status === 403 ? 'Diese Sitzung nimmt niemanden mehr auf.'
                : status === 409 ? 'Diese Sitzung ist voll.'
                : status === 0 ? 'Der Server ist nicht erreichbar.'
                : 'Beitreten hat nicht geklappt.'
        }
    }
    schuelerSitzung.daten = {
        code,
        token: daten.token,
        name: daten.name,
        konfiguration: daten.konfiguration,
        aufgaben: null,
        stand: null
    }
    schuelerSitzung.beendet = false
    schreiben(schuelerSitzung.daten)
    return { ok: true }
}

export function verlassen() {
    clearTimeout(wiederholung)
    schuelerSitzung.daten = null
    schuelerSitzung.beendet = false
    schreiben(null)
}

// Die Ansicht zieht die Aufgaben einmal und legt sie hier ab.
export function aufgabenFesthalten(aufgaben, stand) {
    schuelerSitzung.daten.aufgaben = aufgaben
    standMelden(stand)
}

// --- Stand melden ---
// Immer der ganze Stand. Geht eine Meldung verloren, traegt die naechste alles.
// Laeuft schon eine, wird danach einmal nachgeschickt — mit dem dann neusten Stand.
let unterwegs = false
let nochmal = false
let wiederholung = null

export function standMelden(stand) {
    schuelerSitzung.daten.stand = stand
    schreiben(schuelerSitzung.daten)
    senden()
}

async function senden() {
    if (!schuelerSitzung.daten || schuelerSitzung.beendet) return
    if (unterwegs) {
        nochmal = true
        return
    }
    clearTimeout(wiederholung)
    unterwegs = true
    const { code, token, stand } = schuelerSitzung.daten
    const { status } = await standSenden(code, token, stand)
    unterwegs = false

    if (status === 401 || status === 404) {
        schuelerSitzung.beendet = true
        return
    }
    if (status >= 400 && status < 500) {
        // Der Server lehnt den Stand ab — ein Fehler in der App, kein Netzproblem.
        // Nochmal schicken aendert daran nichts.
        console.error('Sitzung: Stand abgelehnt', status, stand)
        return
    }
    if (status !== undefined) {
        // Netz weg oder Server-Fehler: spaeter mit dem neusten Stand nochmal.
        wiederholung = setTimeout(senden, WIEDERHOLEN_MS)
        return
    }
    if (nochmal) {
        nochmal = false
        senden()
    }
}

// Nach einem Neuladen: den gespeicherten Stand einmal schicken. Das prueft
// zugleich, ob es die Sitzung noch gibt.
if (schuelerSitzung.daten?.stand) senden()

// --- Beendet? ---
// Wer gerade nichts tut, schickt nichts — und merkte nie, dass der Ausbildner
// die Sitzung beendet hat. Darum alle 20 s kurz nachfragen. Netzfehler
// zaehlen nicht als "beendet": die naechste Nachfrage versucht es wieder.
setInterval(async () => {
    const d = schuelerSitzung.daten
    if (!d || schuelerSitzung.beendet) return
    const { status } = await ichPruefen(d.code, d.token)
    // Inzwischen verlassen oder neu beigetreten? Dann gilt die Antwort nicht mehr.
    if (schuelerSitzung.daten !== d) return
    if (status === 401 || status === 404) schuelerSitzung.beendet = true
}, NACHFRAGEN_MS)
